import { useState } from 'react';
import { Container, Row, Col, Card, Button, Image } from 'react-bootstrap';
import { MapPin, Phone, Mail } from 'lucide-react';
import './AboutUs.css'; // Assuming you have a CSS file for custom styles
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Import the timeline components and its CSS
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import Footer from '../components/Footer';


const styles = `
  .facilities-section {
    position: relative;
    overflow: hidden; /* Hides parts of decorative elements that go outside the container */
    padding: 6rem 0;
    background-color: #f8f9fa;
  }

  /* Decorative wavy lines in the top right */
  .facilities-section::before {
    content: '';
    position: absolute;
    top: 2rem;
    right: 2rem;
    width: 100px;
    height: 100px;
    background-image: url("data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M0 10 C 20 0, 40 20, 60 10 S 80 0, 100 10' stroke='%23a29bfe' fill='none' stroke-width='2'/%3e%3cpath d='M0 20 C 20 10, 40 30, 60 20 S 80 10, 100 20' stroke='%23a29bfe' fill='none' stroke-width='2'/%3e%3cpath d='M0 30 C 20 20, 40 40, 60 30 S 80 20, 100 30' stroke='%23a29bfe' fill='none' stroke-width='2'/%3e%3cpath d='M0 40 C 20 30, 40 50, 60 40 S 80 30, 100 40' stroke='%23a29bfe' fill='none' stroke-width='2'/%3e%3cpath d='M0 50 C 20 40, 40 60, 60 50 S 80 40, 100 50' stroke='%23a29bfe' fill='none' stroke-width='2'/%3e%3c/svg%3e");
    opacity: 0.6;
    z-index: 0;
  }

  .image-stack {
    position: relative;
    /* Set a minimum height to contain the absolutely positioned images */
    min-height: 450px; 
  }
  
  /* Decorative red circle behind the images */
  .image-stack::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -30px;
    width: 150px;
    height: 150px;
    border: 15px solid #d63031;
    border-radius: 50%;
    transform: translateY(-50%);
    z-index: 0;
  }

  /* Base styles for images in the stack */
  .image-stack img {
    position: absolute; /* Position images absolutely to allow for overlap */
    border: 5px solid white;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    z-index: 1; /* Default stacking context */
  }

  /* Class for the top image */
  .top-image {
    top: 0;
    right: 0;
    width: 80%;
    z-index: 2; /* Ensure this image is on top */
  }

  /* Class for the bottom image */
  .bottom-image {
    bottom: 0;
    left: 0;
    width: 60%;
  }
`;

const visionariesData = [
    {

        name: 'Dr. RSK Murthy',
        title: 'Founder and Chairman',
        image: '/static/rskm.webp',
        linkSlug: 'dr-rsk-murthy',
    },
    {
        name: 'Dr. M. Ramesh',
        title: 'Chairman',
        image: '/static/drrm.webp',
        linkSlug: 'dr-m-ramesh',
    },
    {
        name: 'Dr. Saraswathi',
        title: 'Director',
        image: '/static/drsr.webp',
        linkSlug: 'dr-s-ramesh',
    }
];

const timelineData = [
    {
        year: '1998',
        subtitle: 'Introduced',
        title: 'The Dawn of a New Era in Laparoscopic Training',
        description: "In 1998, a watershed moment in the history of surgical training in India occurred with the establishment of the Bangalore Endoscopic Surgery, Training Institute & Research Centre, popularly known as the B.E.S.T Institute. Founded with an ambitious motto to democratize the art of laparoscopic surgery, the institute has tirelessly worked to make advanced surgical care accessible even in the most remote corners of the country.",
    },
    {
        year: 'The Visionary:',
        title: "Dr. Makam Ramesh's Global Journey",
        subtitle: 'Expanded',
        description: "The mastermind behind B.E.S.T Institute is Dr. Makam Ramesh, a pioneer of advanced laparoscopic surgery in Karnataka. Before founding the institute, Dr. Ramesh embarked on an exhaustive journey spanning 5 years, visiting more than 26 reputed centers in 7 countries. This international experience allowed him to assimilate diverse techniques and best practices, which later became the foundational pillars of the institute.",
    },
    {
        year: 'The Initial Curriculum:',
        title: 'A Confluence of Global Best Practices',
        subtitle: 'Introduced',
        description: "Upon his return, Dr. Ramesh tailored a comprehensive, practically oriented laparoscopic training course. This course amalgamated the best practices from the various training centers he had visited. Initially designed to train surgeons, gynecologists, and urologists, the curriculum at B.E.S.T Institute is now continually updated to include the latest surgical techniques and technologies.",
    },
    {
        year: 'The Evolution of Training Programs:',
        title: 'Catering to a Diverse Audience',
        subtitle: 'Expanded',
        description: "The courses quickly gained traction among private practitioners, especially those owning nursing homes and small hospitals. Realizing the potential for wider impact, the institute then strategically shifted its focus towards faculty at medical colleges, enabling the benefits of laparoscopy to reach underprivileged communities. This pivot expanded the institute's footprint, attracting medical professionals from all over India and even from countries like Malaysia, Maldives, Sri Lanka, and beyond.",
    },
    {
        year: 'Affordability and Innovation:',
        title: 'A Commitment to Broader Access',
        subtitle: 'Expanded',
        description: "One of the institute's standout achievements has been its focus on affordability. Dr. Ramesh collaborated with several Indian companies to design cost-effective laparoscopic instruments. Innovations did not stop there; the institute developed an 'Endotrainer,' which significantly reduced the cost of training stations from Rs. 15 lakhs to just a few thousands.",
    },
    {
        year: 'Skill Development and Assessment:',
        title: 'The Path to Mastery',
        subtitle: 'Expanded',
        description: "B.E.S.T Institute has been groundbreaking in its pedagogical approach. It has designed unique skill training methods and rigorous assessment modules. Unlike traditional training programs where surgeons learn while operating on patients, the institute provides surgeons with various gadgets and simulators, allowing them to master their skills in a controlled environment before moving on to live surgeries.",
    },
    {
        year: 'The Digital Footprint:',
        title: 'Spreading Knowledge Globally',
        subtitle: 'Expanded',
        description: "The institute made an early foray into digital outreach by launching its website in 1999, at the 1st Asia Pacific Congress of Endoscopic Surgery in Singapore. This digital platform served as a crucial resource for information and communication, and even today with its current website, www.bestinstitute.org.in, it continues to be a valuable resource for surgeons worldwide.",
    },
    {
        year: 'Conclusion:',
        title: 'A Legacy in the Making',
        subtitle: 'Expanded',
        description: "B.E.S.T Institute is not just a training center; it's a movement that has transformed the landscape of laparoscopic surgery in India and across the globe. With over 2,000 trained professionals from five continents, state-endorsed fellowships, and a host of innovations, the institute has set a high benchmark in surgical training. Under the visionary leadership of Dr. Makam Ramesh, the institute has been a beacon of excellence and innovation. As it continues its journey, it remains committed to its foundational principles of quality, inclusivity, and transformative education.",
    },
];

const Facilities = () => {
  return (
    <>
      {/* It's recommended to add these styles to a CSS file */}
      <style>{styles}</style>
      <div className="facilities-section">
        <Container>
          <Row className="align-items-center">
            {/* Left Column: Text Content */}
            <Col lg={6} className="mb-5 mb-lg-0">
              <h1 className="display-4 fw-bold mb-3">Our Facilities</h1>
              <p className="lead text-muted">
                Equipped with state-of-the-art surgical facilities and training
                equipment, B.E.S.T Institute offers a conducive learning
                environment. Our simulation labs and hands-on training
                sessions ensure that our trainees receive an educational
                experience that is both comprehensive and cutting-edge.
              </p>
              <Row className="mt-5">
                <Col xs={6}>
                  <p className="text-muted mb-0">Experience</p>
                  <p className="display-4 fw-bold">12+</p>
                </Col>
                <Col xs={6}>
                  <p className="text-muted mb-0">People</p>
                  <p className="display-4 fw-bold">100+</p>
                </Col>
              </Row>
            </Col>

            {/* Right Column: Image Stack */}
            <Col lg={6}>
              <div className="position-relative image-stack">
                <Image 
                  src="/static/aboutvert2back.webp" 
                  alt="Surgical facility" 
                  fluid 
                  rounded 
                  className="top-image"
                />
                <Image 
                  src="/static/aboutvert2front.webp" 
                  alt="Hands-on training" 
                  fluid 
                  rounded
                  className="bottom-image" 
                />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

function AboutUs() {
    const [validated, setValidated] = useState(false);
      const navigate = useNavigate();


    const handleSubmit = (event) => {
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            event.preventDefault();
            event.stopPropagation();
        }
        setValidated(true);
        // In a real app, you would handle form submission here (e.g., send data to an API)
        if (form.checkValidity() === true) {
            event.preventDefault();
            alert('Form submitted successfully!');
        }
    };

    const handleCardClick = (slug) => {
    // 3. Use the navigate function to go to the new route
    navigate(`/about/${slug}`);
  };

    return (
        <>
            {/* 1. Full-width Hero Image Section */}
            <div className="hero-section text-center text-white" style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/static/OT-Surgery-3-scaled.webp')`, // Replace with your hero image
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                padding: '100px 0',
                minHeight: '40vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Container>
                    <h1 className="display-3 font-weight-bold mb-4">Empowering Surgeons, Advancing Healthcare</h1>
                    <p className="lead mb-5" style={{ maxWidth: '700px', margin: '0 auto 2rem auto' }}>
                        Leaders in Laparoscopic Surgery Training
                    </p>
                    <div>
                        <Link to="/register">
                            <Button variant="primary" size="lg" className="me-3 px-5 py-3">Get Started</Button>
                        </Link>
                        <Link to="/training-programs"> {/* Or any other relevant link */}
                            <Button variant="outline-light" size="lg" className="px-5 py-3">Learn More</Button>
                        </Link>
                    </div>
                </Container>
            </div>

            <div className="history-section mb-5 pb-5">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} className="mb-5 mb-lg-0">
                            
                                <div className="position-relative image-stack">
                                    <Image 
                                    src="/static/aboutvert1back.webp" 
                                    alt="Surgical facility" 
                                    fluid 
                                    rounded 
                                    className="top-image"
                                    />
                                    <Image 
                                    src="/static/aboutvert2front.webp" 
                                    alt="Hands-on training" 
                                    fluid 
                                    rounded
                                    className="bottom-image" 
                                    />
                                </div>
                            
                        </Col>
                        <Col lg={6}>
                            <div className="history-content ps-lg-5">
                                <h1>B.E.S.T Institute & Research Centre</h1>
                                <p className="lead">
                                    Your journey to becoming an expert in laparoscopic surgery starts here. At B.E.S.T Institute, we don't just train surgeons; we shape the future of healthcare.
                                </p>
                                <h2>Our History</h2>
                                <p>
                                    Established in 1998 at A.V. Hospital in Basavanagudi, Bangalore, the B.E.S.T Institute has grown to become a leading institution in the field of endoscopic surgery training and research. Founded by Dr. M Ramesh and Dr. Saraswathi, our institute has trained over 2000 surgeons, fostering excellence in surgical techniques and patient care.
                                </p>
                                <Button href="#timeline" className="read-more-btn">
                                    READ MORE
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            <div className="visionaries-section py-5">
                <Container>
                    <h2 className="text-center mb-5 display-5">Meet Our Visionaries</h2>
                    <Row>
                        {visionariesData.map((visionary, index) => (
                            <Col md={4} key={index} className="mb-4">
                                <Card className="border-0 text-center visionary-card" 
                                onClick={() => handleCardClick(visionary.linkSlug)}
                                style={{ cursor: 'pointer' }} >
                                    <Image src={visionary.image} alt={visionary.name} fluid className="visionary-image" />
                                    <Card.Body>
                                        <Card.Title as="h5" className="mt-3">{visionary.name}</Card.Title>
                                        <Card.Text className="text-muted">{visionary.title}</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </div>

            <Facilities />

            <Container fluid className="mt-5 py-5 bg-light">
                {/* Section 2: Map and Contact Form - Full Width */}
                <Row id="timeline">
                    {/* Full-width Map */}
                    <Col>
                        <VerticalTimeline lineColor="#e9ecef">
                            {timelineData.map((data, idx) => (
                                <VerticalTimelineElement
                                    key={idx}
                                    className="vertical-timeline-element--work"
                                    contentStyle={{ background: '#ffffff', color: '#343a40', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}
                                    contentArrowStyle={{ borderRight: '7px solid  #ffffff' }}
                                    date={
                                        <>
                                            {data.year && <span style={{ fontWeight: 'bold' }}>{data.year}</span>}
                                            {data.subtitle && <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', color: '#6c757d' }}>{data.subtitle}</div>}
                                        </>
                                    }
                                    iconStyle={{ background: '#0d6efd', color: '#fff' }}
                                    icon={<Clock />}
                                >
                                    <h4 className="vertical-timeline-element-title" style={{ fontWeight: 'bold' }}>{data.title}</h4>
                                    {/* Subtitle is no longer here */}
                                    <p style={{ color: '#6c757d', fontSize: '0.95rem' }}>
                                        {data.description}
                                    </p>
                                </VerticalTimelineElement>
                            ))}
                        </VerticalTimeline>
                    </Col>
                </Row>
            </Container>

            <Footer />
        </>
    );
}

export default AboutUs;